import { LoginSuccess } from '@/components/animations';
import { LinkButton } from '@/components/elements/forms/buttons';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import CardWrapper from '@/components/modules/CardWrapper';
import EmptyState from '@/components/templates/EmptyState';
import CheckoutDetails from '@/components/templates/giftCards/CheckoutDetails/CheckoutDetails';
import { trackMpEvent, trackPage } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { ProductType } from '@/pages/presentkort/GiftcardCheckout.types';
import { giftCardServices } from '@/services';
import { Order, PRODUCT_TYPE } from '@/types/api/services/giftcard';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const baseTranslationsKey = 'pages.giftcard.confirmation';

const getScreenName = (productType: Order['productType']) => {
  switch (productType) {
    case PRODUCT_TYPE.UGC:
      return 'gift_card_confirmation';
    case PRODUCT_TYPE.UGCW:
      return 'wellness_card_confirmation';
    case PRODUCT_TYPE.EGC:
      return 'place_gift_card_confirmation';
    case PRODUCT_TYPE.VCEGC:
      return 'value_card_confirmation';
    default:
      const _exhaustiveCheck: never = productType;
      throw new Error(`Unhandled product type: ${_exhaustiveCheck}`);
  }
};

const getProductType = (productType: Order['productType']): ProductType => {
  switch (productType) {
    case PRODUCT_TYPE.UGC:
      return 'giftcard';
    case PRODUCT_TYPE.UGCW:
      return 'wellnesscard';
    case PRODUCT_TYPE.EGC:
      return 'placecard';
    case PRODUCT_TYPE.VCEGC:
      return 'valuecard';
    default:
      const _exhaustiveCheck: never = productType;
      throw new Error(`Unhandled product type: ${_exhaustiveCheck}`);
  }
};

const GiftcardConfirmation = () => {
  const { isMobileView } = useMobileView();
  const location = useLocation();
  const [order, setOrder] = useState<Order>(null);
  const selectedPaymentMethod = location.state?.selectedPaymentMethod;
  const guestEmail = location.state?.guestEmail;
  const isGuest = Boolean(guestEmail);
  const { id, slugId } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    giftCardServices
      .getGiftcardOrder(id)
      .then((response) => {
        setOrder(response);
        setIsLoading(false);
        trackPage();
        trackMpEvent('screen_shown', {
          screen_name: getScreenName(response.productType),
        });
      })
      .catch((error) => {
        Sentry.captureException(error);
        toast(({ closeToast }) => <Snackbar label={_s('serverError')} type="danger" onClose={closeToast} />, {
          autoClose: 5000,
        });
        setIsLoading(false);
      });
  }, [id]);

  if (isLoading) return <LoadingPlaceHolder text={_s(`${baseTranslationsKey}.loading`)} />;

  return (
    <PageViewLayout
      type="subView"
      wrapperClass={`min-h-screen ${isMobileView ? 'bg-brown-50' : 'bg-gradient'}`}
      rightSlot={
        <Link to={'/'}>
          <Icon variant="close-alt" alt={_s('close')} size="sm" />
        </Link>
      }>
      {order && (
        <div className="bg-brown-50 flex justify-center lg:bg-transparent">
          <div className="lg:py-xxl w-full lg:container">
            <div className="gap-xxl flex justify-center">
              <div className={`gap-lg flex grow-[9999] flex-col lg:max-w-[600px]`}>
                <CardWrapper className="w-full">
                  <EmptyState
                    icon={<LoginSuccess style={{ width: 196, height: 154 }} />}
                    title={_s(`${baseTranslationsKey}.title`)}
                    {...((order.email || guestEmail) && {
                      body: _s(`${baseTranslationsKey}.body`, { email: isGuest ? guestEmail : order.email }),
                    })}
                  />
                </CardWrapper>
                <div className="lg:hidden">
                  <CardWrapper className="w-full bg-white">
                    <CheckoutDetails
                      discount={order.discount}
                      deliveryType={order.deliveryType}
                      productType={getProductType(order.productType)}
                      selectedAmount={parseFloat(order.price)}
                      selectedQuantity={order.quantity}
                      paymentMethod={selectedPaymentMethod ?? { type: order.paymentMethod }}
                    />
                  </CardWrapper>
                </div>
                <CardWrapper className="w-full bg-white">
                  <div className="p-lg gap-lg flex flex-col">
                    <LinkButton
                      to={(() => {
                        if (order.productType === PRODUCT_TYPE.EGC || order.productType === PRODUCT_TYPE.VCEGC) {
                          return `/places/${slugId}`;
                        }
                        return '/';
                      })()}
                      block
                      variant="primary"
                      label={_s(`done`)}
                      size="md"
                    />
                  </div>
                </CardWrapper>
              </div>
              <div className="sticky hidden w-full lg:flex lg:max-w-[390px] lg:grow-[100] lg:flex-col">
                <CardWrapper className="w-full bg-white">
                  <CheckoutDetails
                    discount={order.discount}
                    deliveryType={order.deliveryType}
                    productType={getProductType(order.productType)}
                    selectedAmount={parseFloat(order.price)}
                    selectedQuantity={order.quantity}
                    paymentMethod={selectedPaymentMethod ?? { type: order.paymentMethod }}
                  />
                </CardWrapper>
              </div>
            </div>
          </div>
        </div>
      )}
      <Helmet>
        <title>{`Bekräftelse av ${order?.productType === 1 ? 'presentkorts' : 'friskvårdskorts'}köp`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
    </PageViewLayout>
  );
};

export default GiftcardConfirmation;
